import { useMemo } from "react";

import { useUserOrganizations, useWorkspaces } from "src/api/queries";
import { WorkspaceDataplane } from "src/clients/flow-api";
import { UserProvisioningMode } from "src/clients/taktile-api";
import { OrgWithWorkspaces } from "src/layout/types";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const useWorkspaceAndOrg = () => {
  const { data: rawOrgs, isLoading: isOrgsLoading } = useUserOrganizations();
  const { data: workspaces, isLoading: isWorkspacesLoading } = useWorkspaces();
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();

  const workspace = useMemo(() => {
    if (workspaces) {
      return workspaces.find((ws) => ws.id === wsId) ?? null;
    }

    return null;
  }, [workspaces, wsId]);

  const orgs = useMemo(() => {
    if (!rawOrgs || !workspaces) {
      return [];
    }

    const workspacesByOrgId = workspaces.reduce(
      (acc, workspace) => {
        const orgWorkspaces: WorkspaceDataplane[] = acc[
          workspace.organization_id
        ]
          ? acc[workspace.organization_id]
          : [];
        return {
          ...acc,
          [workspace.organization_id]: [...orgWorkspaces, workspace],
        };
      },
      {} as Record<string, WorkspaceDataplane[]>,
    );

    return rawOrgs
      .map(
        (rawOrg) =>
          ({
            ...rawOrg,

            workspaces: (workspacesByOrgId[rawOrg.id] || []).sort((a, b) =>
              a.name.localeCompare(b.name),
            ),
          }) as OrgWithWorkspaces,
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rawOrgs, workspaces]);

  const org = useMemo(() => {
    if (orgs) {
      return orgs.find((userOrg) => userOrg.id === orgId) || null;
    }

    return null;
  }, [orgs, orgId]);

  return {
    workspace,
    workspaces,
    org,
    orgs,
    isLoading: isOrgsLoading || isWorkspacesLoading,
  };
};

export const useIsScimOnly = () => {
  const { org } = useWorkspaceAndOrg();
  return org?.user_provisioning_mode === UserProvisioningMode.SCIM_ONLY;
};
